import React, { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useQueryClient } from 'react-query';
import Draggable from 'react-draggable';
import ControlPanel from './components/ControlPanel/ControlPanel';
import PhoneBtn from './components/PhoneBtn/PhoneBtn';
import { mainContainerId, notDraggableClass } from './constants/common';
import { updatePos } from './utils/updatePos';
import useSipStates from '../../hooks/useSipStates';
import {
  setCallDirection,
  setIsControlPanelVisible,
  setNumPadVisible,
  setSubscriber,
} from './store/actions';
import useCalls from './queries/useCalls';
import {
  isControlPanelVisibleSelector,
  numPadVisibleSelector,
} from './store/selectors';
import useRingtoneDevices from './hooks/useRingtoneDevices';
import useRingtoneElement from './hooks/useRingtoneElement';
import { phoneQueryKeys } from './queries/queryKeys';
import styles from './Phone.module.scss';
import useCompanies from '../../queries/useCompanies';

const defaultPos = { x: 0, y: 0 };

const Phone = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const containerRef = useRef();
  const { isConnected, isIdle } = useSipStates();

  const [pos, setPos] = useState(defaultPos);
  const [isMove, setIsMove] = useState(false);

  useRingtoneDevices();
  useRingtoneElement();

  useCalls({
    enabled: isConnected,
    staleTime: 1000,
    refetchInterval: 1000,
    refetchIntervalInBackground: true,
  });

  useCompanies();

  const isControlPanelVisible = useSelector(isControlPanelVisibleSelector);
  const numPadVisible = useSelector(numPadVisibleSelector);

  const handlePhoneBtnClick = () => {
    if (isMove) return;
    dispatch(setIsControlPanelVisible(true));
  };

  const handleHideControlPanel = () => {
    dispatch(setIsControlPanelVisible(false));
    setPos(defaultPos);

    if (numPadVisible) {
      dispatch(setNumPadVisible(false));
    }
  };

  const handleDrag = (_e, ui) => {
    setIsMove(true);
    setPos((prev) => ({
      x: prev.x + ui.deltaX,
      y: prev.y + ui.deltaY,
    }));
  };

  const handleStart = () => {
    setIsMove(false);
  };

  const handleIntersect = async (entries, observerElement) => {
    const entry = entries[0];

    const { isIntersecting, target } = entry;

    if (!isIntersecting) {
      const newPos = updatePos(pos, target);
      setPos(newPos);
    } else {
      observerElement.unobserve(containerRef.current);
    }
  };

  // Сбрасываем абонента и направление звонка
  useEffect(() => {
    if (!isConnected || isIdle) {
      dispatch(setCallDirection(null));
      dispatch(setSubscriber(null));
    }
  }, [isConnected, isIdle, dispatch]);

  // Сбрасываем звонки при размонтировании и если нет подключения
  useEffect(() => {
    if (!isConnected) {
      queryClient.removeQueries(phoneQueryKeys.calls());
    }
    return () => queryClient.removeQueries(phoneQueryKeys.calls());
  }, [isConnected]);

  return (
    <Draggable
      onStart={handleStart}
      onDrag={handleDrag}
      position={pos}
      cancel={`.${notDraggableClass}`}
    >
      <div
        className={styles.root}
        ref={containerRef}
        id={mainContainerId}
      >
        {isControlPanelVisible ? (
          <ControlPanel
            onHide={handleHideControlPanel}
            containerRef={containerRef}
            onIntersect={handleIntersect}
          />
        ) : (
          <PhoneBtn onClick={handlePhoneBtnClick} />
        )}
      </div>
    </Draggable>
  );
};

export default Phone;
